import React from 'react';

import { Flex } from '@reckon-web/flex';
import { Notice } from '@reckon-web/notice';
import { Text } from '@reckon-web/text';
import { makeId } from '@reckon-web/utils';

import type { Job } from '../../../hooks/use-query-job';
import { withUser } from '../../../hocs/with-user';
import { DueDate } from '../../common/due-date';
import { JobSignOffStatus } from '../../../hooks/use-query-signoffs';

const User = withUser(({ user }) => (
  <Text
    size="small"
    weight="medium"
    /* TODO WORKFLOW-400 make 'user' a testId prop?  */
    data={{ testid: makeId('user', 'user-name') }}
  >
    {user?.fullName}
  </Text>
));

export const SignOffStatus: React.FC<{ job: Job; testId?: string }> = ({
  job,
  testId = `sign-off-status`,
}) => {
  switch (job.signOffStatus) {
    default:
      return null;
    case JobSignOffStatus.Requested:
      return (
        <Flex>
          <Notice
            tone="accent"
            title="Sign off requested"
            data-testid={makeId(testId, 'notice-requested')}
          >
            <Flex alignItems="center" gap="xsmall">
              {job.signOffRequestedByUserId && (
                <User
                  userId={job.signOffRequestedByUserId}
                  data-testid={makeId(testId, 'user-requested')}
                />
              )}
              <Text size="small">&bull;</Text>
              <DueDate
                date={new Date(job.signOffRequestedDateUtc)}
                testId={makeId(testId, 'due-date-requested')}
              />
            </Flex>
          </Notice>
        </Flex>
      );
    case JobSignOffStatus.Approved:
      return (
        <Flex>
          <Notice
            tone="positive"
            title="Job has been signed off"
            data-testid={makeId(testId, 'notice-approved')}
          >
            <Flex alignItems="center" gap="xsmall">
              {job.signOffActionedByUserId && (
                <User
                  userId={job.signOffActionedByUserId}
                  data-testid={makeId(testId, 'user-approved')}
                />
              )}
              <Text size="small" weight="medium">
                &bull;
              </Text>
              <DueDate
                date={new Date(job.signOffActionedDateUtc)}
                testId={makeId(testId, 'due-date-approved')}
              />
            </Flex>
          </Notice>
        </Flex>
      );
    case JobSignOffStatus.Revoked:
      return (
        <Flex>
          <Notice
            tone="cautious"
            title="Sign off revoked"
            data-testid={makeId(testId, 'notice-revoked')}
          >
            <Flex alignItems="center" gap="xsmall">
              {job.signOffActionedByUserId && (
                <User
                  userId={job.signOffActionedByUserId}
                  data-testid={makeId(testId, 'user-revoked')}
                />
              )}
              <Text size="small">&bull;</Text>
              <DueDate
                date={new Date(job.signOffActionedDateUtc)}
                testId={makeId(testId, 'due-date-revoked')}
              />
            </Flex>
          </Notice>
        </Flex>
      );
    case JobSignOffStatus.Declined:
      return (
        <Flex>
          <Notice
            tone="critical"
            title="Sign off request declined"
            data-testid={makeId(testId, 'notice-declined')}
          >
            <Flex alignItems="center" gap="xsmall">
              {job.signOffActionedByUserId && (
                <User
                  userId={job.signOffActionedByUserId}
                  data-testid={makeId(testId, 'user-declined')}
                />
              )}
              <Text size="small">&bull;</Text>
              <DueDate
                date={new Date(job.signOffActionedDateUtc)}
                testId={makeId(testId, 'due-date-declined')}
              />
            </Flex>
          </Notice>
        </Flex>
      );
  }
};
